import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import BreadcrumbsCustom from 'src/components/common/BreadcrumbsCustom';
import InputCustom from 'src/components/common/field/InputCustom';
import DropDown from 'src/components/common/field/DropDown';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DropDownStatus from 'src/components/master/DropDownStatus';
import Record from 'src/components/master/Record';
import DialogForm from 'src/components/master/desiredTreatment/DialogForm';
import ConfirmDelete from 'src/components/common/modal/ConfirmDelete';

import Pagin, { PaginationType } from 'src/components/common/Pagin';
import { postAPI, postFormDataAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import { ItemType } from 'src/components/common/field/TypeCustom';
import { getField } from 'src/common/helper';
import { FieldTableType } from 'src/components/master/TypeCustom';
import { useNotify } from 'src/context/ContextNotify';

const sxlabelDropdown = {
  '&.Mui-focused': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
};

const sizeDropdown = {
  width: '160px',
  height: '32px'
};


const breadcrumbsDefine = [
  {
    to: '/',
    label: '会計'
  },
  {
    label: '希望施術マスタ設定一覧'
  }
];

const defaultFilter = {
  categoryid: null,
  status: 1,
  surgery: null
};

const paginationDefault = {
  first: false,
  formatTotal: '0',
  last: false,
  page: 1,
  pages: 1,
  pagesize: 20,
  total: 0
};


const fieldsDefine: FieldTableType[] = [
  {
    label: 'カテゴリー',
    field: 'category',
    align: 'left',
    width: '10%'
  },
  {
    label: '希望施術',
    field: 'part',
    align: 'left',
    width: '30%'
  },
  {
    label: 'メモ',
    field: 'note',
    align: 'left',
    width: '25%'
  },
  {
    label: '見積なし予約可否',
    field: 'displayorder',
    align: 'left',
    width: '15%'
  },
  {
    label: 'ステータス',
    field: 'status',
    align: 'center',
    width: '10%',
    isStatus: true
  },
  {
    label: '',
    field: '',
    align: 'left',
    width: '10%',
    isColFinal: 'part'
  }
];

type DataDialogForm = {
  id: number | undefined;
  open: boolean;
};

type FilterType = {
  categoryid: string | null;
  status: number | null;
  surgery: string | null;
};

const MasterDesiredTreatment = () => {
  const notifyContext = useNotify();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [filter, setFilter] = useState<FilterType>(defaultFilter);
  const [categorys, setCategorys] = useState<ItemType[]>([]);
  const [datas, setDatas] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationType>(paginationDefault);
  const [dataDialogConfirm, setdataDialogConfirm] = useState<DataDialogForm>({
    id: undefined,
    open: false
  });
  const [dataDialogForm, setdataDialogForm] = useState<DataDialogForm>({
    id: undefined,
    open: false
  });

  const handleClickForm = (data: any | undefined = undefined) => {
    setdataDialogForm({
      id: getField(data, 'id', 0),
      open: true
    });
  };

  const handleCloseForm = (isRefreshList: boolean = false) => {
    if (isRefreshList) {
      getList();
    }
    setdataDialogForm({
      id: undefined,
      open: false
    });
  };

  const handleCloseConfirm = () => {
    setdataDialogConfirm({
      id: undefined,
      open: false
    });
  };

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
      }))
      : [];

    return converted;
  };

  const handleChangeFilter = (name: string) => (id: number | string | null) => {
    setFilter(prevState => ({
      ...prevState,
      [name]: id !== '' ? id : null
    }))
  };

  const handlePageChange = (eventL: any, value: number) => {
    getList(value);
  };

  const handleSearch = () => {
    getList();
  };

  const getList = (page?: number) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        ...filter,
        page: page ? page : pagination.page,
        pagesize: pagination.pagesize
      };

      setIsLoading(true);
      postAPI(`/master/part-search?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDatas(getField(res.data, 'items', []));
            setPagination(getField(res.data, 'pagination', {}));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getCategory = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        category: '',
        page: 1,
        pagesize: 100,
        status: 1,
        type: 'part'
      }

      setIsLoadingSearch(true);
      postAPI(`/master/category-search?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            const cate = getField(res.data, 'items', []);
            setCategorys(convertItemType(cate, 'id', 'category'));
          }
        })
        .finally(() => {
          setIsLoadingSearch(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const callAPIDelete = (id: number | undefined) => {
    if (id) {
      try {
        const token = getToken();

        if (!token) {
          throw new Error('Failed to get access token');
        }

        const data: FormData = new FormData();
        data.append('id', String(id));

        postFormDataAPI(`/master/part-delete?token=${token}`, data)
          .then((res) => {
            handleCloseConfirm();

            if (!res) {
              notifyContext.setNotifyMessage({
                show: true,
                message: 'Error API',
                type: 'error'
              });
              return;
            }

            if (res.code === 200 && res.data) {
              notifyContext.setNotifyMessage({
                show: true,
                message: '削除しました。',
                type: 'success',
              });
              getList();
            }
            if (res.code === 501 || res.code === 500) {
              notifyContext.setNotifyMessage({
                show: true,
                message: res.msg,
                type: 'error'
              });
            }
          })
          .finally(() => {
          });
      } catch (error) {
        console.error('Failed to fetch data:', error);
        throw error;
      }
    }
  };

  const handleClickDelete = (datas: any | undefined) => {
    if (datas) {
      const id = Number(getField(datas, 'id', 0));

      setdataDialogConfirm({
        id: id,
        open: true
      });
    }
  };

  useEffect(() => {
    getCategory();
    getList();

    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <BreadcrumbsCustom breadcrumbs={breadcrumbsDefine} />
      <Typography
        component='label'
        sx={{
          fontWeight: '500',
          fontSize: '20px',
          lineHeight: '24px'
        }}
      >
        {'希望施術マスタ設定'}
      </Typography>

      <Stack
        direction={'row'}
        spacing='8px'
        sx={{
          mt: '18px'
        }}
      >
        <Stack
          direction={'row'}
          spacing='8px'
        >
          {!isLoadingSearch ? (
            <DropDown
              selected={filter.categoryid}
              idSelect='categoryid'
              placeholder='カテゴリー'
              defaultSelect={'選択する'}
              onChange={handleChangeFilter('categoryid')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              items={categorys}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!isLoadingSearch ? (
            <InputCustom
              name='surgery'
              value={filter.surgery}
              onChange={handleChangeFilter('surgery')}
              type='text'
              labelInput='希望施術'
              sx={{ width: '320px', height: '32px', minHeight: '32px' }}
              sxInput={{ height: '0em' }}
            />
          ) : (
            <Skeleton variant='rectangular' width={'320px'} height={'32px'} />
          )}

          {!isLoadingSearch ? (
            <DropDownStatus
              selected={filter.status}
              onChange={handleChangeFilter('status')}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!isLoadingSearch ? (
            <BtnGradient
              text='検索'
              handleButtonClick={handleSearch}
              sx={{
                minWidth: '60px',
                width: '60px',
              }}
            />
          ) : (
            <Box width={'60px'}>
              <Skeleton variant='rectangular' width={'60px'} height={'32px'} />
            </Box>
          )}
        </Stack>

        <Stack
          direction={'row'}
          justifyContent={'right'}
          width={'100%'}
        >
          {!isLoading ? (
            <BtnOutlineGradient
              text='新規作成'
              srcStartIcon='/images/ic-plus-blue.svg'
              handleButtonClick={() => handleClickForm()}
              sx={{
                width: '120px'
              }}
            />
          ) : (
            <Box width={'120px'}>
              <Skeleton variant='rectangular' width={'120px'} height={'32px'} />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box sx={{
        mt: '27px'
      }}>
        {!isLoading ? (
          <Record
            items={datas}
            fields={fieldsDefine}
            isLoading={isLoading}
            onClickRow={handleClickForm}
            onClickButton={handleClickDelete}
          />
        ) : (
          <Skeleton variant='rounded' width={'100%'} height={'calc(100vh - 320px)'} />
        )}
      </Box>
      {datas.length > 0 && pagination && pagination.page && (
        <Pagin handlePageChange={handlePageChange} pagination={pagination} />
      )}

      <DialogForm
        open={dataDialogForm.open}
        id={dataDialogForm.id}
        onClose={handleCloseForm}
      />

      <ConfirmDelete
        openConfirm={dataDialogConfirm.open}
        handleCloseConfirmDelete={handleCloseConfirm}
        handleDelete={() => callAPIDelete(dataDialogConfirm.id)}
      />
    </Box>
  );
}

export default MasterDesiredTreatment;